/**
 * Publishes the module-level `authStore` into the React tree so components re-render on
 * sign-in/out, and holds the principal resolved from `GET /api/auth/me`.
 *
 * The store stays the source of truth for the token; this context only mirrors it and owns
 * the principal (which the store deliberately knows nothing about — identity comes from
 * `/me`, never from decoding the JWT client-side).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authApi } from '@/api/auth';
import { getAccessToken, registerSignOutCallback, setAccessToken } from './authStore';
import { logoutAndRestartLogin } from './loginKickoff';
import { principalFromMe, type Principal } from './principal';

export type AuthStatus = 'booting' | 'authed' | 'unauthed';

/**
 * Resolution state of `GET /api/auth/me`, kept separate from `principal` because a null
 * principal is ambiguous on its own: it means "still resolving" on boot and "identity could
 * not be loaded" after a failure, and the UI has to render those differently.
 */
export type PrincipalStatus = 'pending' | 'ready' | 'error';

interface AuthContextValue {
  status: AuthStatus;
  principal: Principal | null;
  principalStatus: PrincipalStatus;
  /**
   * Publish an access token as the live session. `expiresAt` is **required** (epoch ms, or
   * an explicit `null` when the response carried no expiry) because this writes through to
   * the store: a defaulted parameter here would silently clobber an expiry that an earlier
   * `adoptTokenResponse` had just captured. When adopting a token the store already holds,
   * pass `getExpiresAt()`.
   */
  setAuthed: (token: string, expiresAt: number | null) => void;
  setUnauthed: () => void;
  /**
   * Resolve the principal from `GET /api/auth/me`. Handles the tenant and the platform
   * operator shapes. Returns null (and leaves the session alone) when `/me` fails — the
   * 401 path is already owned by the client's silent refresh — and moves
   * `principalStatus` to `'error'` so the UI can offer a retry. Safe to call again to retry.
   */
  bootstrapPrincipal: () => Promise<Principal | null>;
  /** `POST /api/auth/logout` → clear the in-memory session → restart the login kickoff. */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export interface AuthProviderProps {
  children: ReactNode;
  initialStatus?: AuthStatus;
}

export function AuthProvider({ children, initialStatus }: AuthProviderProps) {
  const [status, setStatus] = useState<AuthStatus>(
    () => initialStatus ?? (getAccessToken() ? 'authed' : 'booting'),
  );
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [principalStatus, setPrincipalStatus] = useState<PrincipalStatus>('pending');

  useEffect(() => {
    registerSignOutCallback(() => {
      setStatus('unauthed');
      setPrincipal(null);
      setPrincipalStatus('pending');
    });
    return () => registerSignOutCallback(null);
  }, []);

  const setAuthed = useCallback((token: string, expiresAt: number | null) => {
    setAccessToken(token, expiresAt);
    setStatus('authed');
  }, []);

  const setUnauthed = useCallback(() => {
    setAccessToken(null);
    setStatus('unauthed');
    setPrincipal(null);
    setPrincipalStatus('pending');
  }, []);

  const bootstrapPrincipal = useCallback(async () => {
    setPrincipalStatus('pending');
    try {
      const next = principalFromMe(await authApi.me());
      setPrincipal(next);
      setPrincipalStatus('ready');
      return next;
    } catch {
      // A dead session has already been handled by the client (refresh → kickoff); any
      // other failure leaves the principal unresolved for the UI to surface.
      setPrincipalStatus('error');
      return null;
    }
  }, []);

  const signOut = useCallback(async () => {
    await logoutAndRestartLogin();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      principal,
      principalStatus,
      setAuthed,
      setUnauthed,
      bootstrapPrincipal,
      signOut,
    }),
    [status, principal, principalStatus, setAuthed, setUnauthed, bootstrapPrincipal, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export function usePrincipal(): Principal | null {
  return useAuth().principal;
}
